/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useCallback, useMemo, useState } from "react";
import { useAppContext } from "../../../hooks/useAppContext";
import { useCalendarDataSources } from "../../../hooks/useCalendarDataSources";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useLabels } from "../../../hooks/useLabels";
import { useUserEventsActions } from "../../../hooks/useUserEvents";
import { useUserEventsValidation } from "../../../hooks/useUserEventsValidation";
import { Button } from "../../button";
import { AlertDialog } from "../../dialog/alert/alert-dialog";
import { ConfirmDialog } from "../../dialog/confirm";
import { labels } from "./labels";
import {
	SpecialDay,
	useUserDateDialogActions,
	useUserDateDialogState,
} from "./use-user-date-dialog-state";
import { useSnackbar } from "../../../hooks/useSnackbar";

interface IFooter {
	closeDialog: (data: eXpress.creator.calendars.ICalendarDataSources | undefined) => void;
}

const addedUserDates: eXpress.core.IUserDate[] = [];

export const Footer: FC<IFooter> = ({ closeDialog }) => {
	const {
		context: { language },
	} = useAppContext();
	const [isSaving, setIsSaving] = useState(false);
	const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
	const [isUnsavedProgressDialogOpen, setIsUnsavedProgressDialogOpen] = useState(false);
	const [message, setMessage] = useState<string>();
	const { createErrorHandler, createFatalErrorHandler } = useErrorHandling();

	const { addSnackbar } = useSnackbar();

	const [
		[
			saveText,
			dismissText,
			addEventText,
			editEventText,
			cancelEventText,
			warningBackButtonText,
			warningTitle,
			warningAmountOfEvents,
			warningUnusedUserDates,
			warningTextTooLong,
			warningEventsTitle,
			toastDuplicateEvent,
		],
	] = useLabels([
		labels.saveButton,
		labels.dismissButton,
		labels.addButton,
		labels.editButton,
		labels.cancelButton,
		labels.warningBackButton,
		labels.warningTitle,
		labels.warningAmountOfEvents.description,
		labels.warningUnusedUserDates.description,
		labels.warningTextTooLong.description,
		labels.warningEventsTitle.description,
		labels.toastDuplicateEvents.description,
	]);

	const {
		selectedUserDates,
		deletedUserDateIds,
		newUserDateIds,
		userDates,
		updatedUserDateIds,
		specialDays,
		formValues,
		selectedUserDate,
	} = useUserDateDialogState();

	const { updateUserDate, addUserDate, clearForm, clear, deleteUserDate } =
		useUserDateDialogActions();

	const { specialDays: specialDatesFromDefinition } = useCalendarDataSources() || {};

	const { addUserDatesAsync, deleteUserDatesAsync, updateUserDatesAsync } =
		useUserEventsActions();

	const validateUserDates = useUserEventsValidation();

	const handleUpdateProduct = useCallback(async () => {
		await Promise.all([
			newUserDateIds.size
				? addUserDatesAsync(
						Array.from(newUserDateIds).map((userDateId) => userDates.get(userDateId)!),
				  )
				: undefined,
			deletedUserDateIds.size
				? deleteUserDatesAsync(Array.from(deletedUserDateIds))
				: undefined,
			updatedUserDateIds.size
				? updateUserDatesAsync(
						Array.from(updatedUserDateIds).map(
							(userDateId) => userDates.get(userDateId)!,
						),
				  )
				: undefined,
		]).catch(createFatalErrorHandler());

		clear();
		clearForm();
		closeDialog({
			UserDate: selectedUserDates,
			...Object.keys(specialDatesFromDefinition || {}).reduce<Record<string, unknown>>(
				(p, key) => {
					const show = specialDays.get(key as SpecialDay);
					// if no value set in the dialog, we remain the original setting from definition
					if (show !== undefined) {
						p[key] = {
							show,
						};
					}
					return p;
				},
				{},
			),
		});
	}, [
		newUserDateIds,
		addUserDatesAsync,
		deletedUserDateIds,
		deleteUserDatesAsync,
		updatedUserDateIds,
		updateUserDatesAsync,
		createFatalErrorHandler,
		clear,
		clearForm,
		closeDialog,
		selectedUserDates,
		specialDatesFromDefinition,
		userDates,
		specialDays,
	]);

	const getLabelForWarningType = useCallback(
		(type: eXpress.creator.calendars.calendarUtils.UserDatesWarningType) => {
			switch (type) {
				case eXpress.creator.calendars.calendarUtils.UserDatesWarningType.TextTooLong:
					return warningTextTooLong ?? labels.warningTextTooLong.description.default;
				case eXpress.creator.calendars.calendarUtils.UserDatesWarningType.AmountOfEvents:
					return (
						warningAmountOfEvents ?? labels.warningAmountOfEvents.description.default
					);
				case eXpress.creator.calendars.calendarUtils.UserDatesWarningType.UnusedUserDates:
					return (
						warningUnusedUserDates ?? labels.warningUnusedUserDates.description.default
					);
				default:
					return "";
			}
		},
		[warningAmountOfEvents, warningTextTooLong, warningUnusedUserDates],
	);

	const validateProduct = useCallback(async () => {
		setIsSaving(true);
		const userEventData = new eXpress.collections.Dictionary<
			eXpress.RecurringDateCode,
			eXpress.core.IUserDate[]
		>();

		selectedUserDates.forEach((userDate) => {
			const valuesForDate = userEventData.get(userDate.dateCode);
			if (valuesForDate) valuesForDate.push(userDate);
			userEventData.set(userDate.dateCode, valuesForDate ?? [userDate]);
		});

		const errors = await validateUserDates(userEventData).catch(createErrorHandler());

		if (!errors?.length) {
			await handleUpdateProduct();
		} else {
			setIsAlertDialogOpen(true);

			let msg = `<p>${warningEventsTitle}</p><br>`;

			errors.forEach((error, index) => {
				const warningLabel = getLabelForWarningType(error.warningType);
				msg += `<p>${warningLabel}</p><ul>`;
				error.invalidDateCodes.forEach((code) => {
					msg += `<li>${new Date(
						// Hardcoded leap year so we can display 29 Feb
						2024,
						eXpress.RecurringDateCode.getMonth(code) - 1,
						eXpress.RecurringDateCode.getDay(code),
					).toLocaleDateString(language, {
						month: "long",
						day: "numeric",
					})}</li>`;
				});
				msg += "</ul>";
				if (index < errors.length) msg += "<br>";
			});

			setMessage(msg);
		}
		setIsSaving(false);
	}, [
		createErrorHandler,
		getLabelForWarningType,
		handleUpdateProduct,
		language,
		selectedUserDates,
		validateUserDates,
		warningEventsTitle,
	]);

	const footerState = useMemo(() => {
		if (selectedUserDate) return "edit";
		if (Object.values(formValues).some((val) => !!val)) return "add";
		return "confirm";
	}, [formValues, selectedUserDate]);

	const handleFormSubmit = useCallback(() => {
		if (!Object.values(formValues).every((val) => !!val)) {
			// TODO form validation
		} else {
			const dateCode = eXpress.RecurringDateCode.create(formValues.day!, formValues.month!);

			if (!selectedUserDate?.id) {
				const newGuid = addUserDate({
					dateCode,
					value: formValues.title!,
				});

				if (!newGuid)
					addSnackbar({
						id: "duplicate-event",
						text:
							toastDuplicateEvent ?? labels.toastDuplicateEvents.description.default,
					});
				addedUserDates.push({
					id: newGuid,
					dateCode,
					value: formValues.title!,
				});
			} else {
				updateUserDate({
					dateCode,
					value: formValues.title!,
					id: selectedUserDate.id,
				});
			}
			clearForm();
		}
	}, [
		formValues,
		selectedUserDate?.id,
		clearForm,
		addUserDate,
		addSnackbar,
		toastDuplicateEvent,
		updateUserDate,
	]);

	const handleDismiss = useCallback(() => {
		if (newUserDateIds.size || updatedUserDateIds.size || deletedUserDateIds.size) {
			setIsUnsavedProgressDialogOpen(true);
		} else {
			clear();
			clearForm();
			closeDialog(undefined);
		}
	}, [
		clear,
		clearForm,
		closeDialog,
		deletedUserDateIds.size,
		newUserDateIds.size,
		updatedUserDateIds.size,
	]);

	const buttonText = useMemo(() => {
		switch (footerState) {
			case "edit":
				return editEventText;
			case "add":
				return addEventText;
			case "confirm":
			default:
				return saveText;
		}
	}, [addEventText, editEventText, footerState, saveText]);

	return (
		<>
			<Button
				variant="filled"
				onClick={footerState === "confirm" ? validateProduct : handleFormSubmit}
				type={footerState === "confirm" ? "button" : "submit"}
				id="event-form-button"
				disabled={isSaving}
				{...(footerState !== "confirm" && { form: "event-form" })}
			>
				{buttonText}
			</Button>
			<Button
				id="event-cancel-button"
				onClick={footerState === "confirm" ? handleDismiss : clearForm}
				style={{ marginRight: "8px" }}
			>
				{footerState === "confirm" ? dismissText : cancelEventText}
			</Button>
			<AlertDialog
				ok={warningBackButtonText}
				title={warningTitle}
				messageHtml={message}
				isOpen={isAlertDialogOpen}
				onOk={() => setIsAlertDialogOpen(false)}
			/>
			<ConfirmDialog
				cancelLabel={labels.warningUnsavedChanges.dontSaveButton}
				isOpen={isUnsavedProgressDialogOpen}
				okLabel={labels.warningUnsavedChanges.saveButton}
				isOkDisabled={isSaving}
				onCancel={() => {
					addedUserDates.forEach((userDate) => deleteUserDate(userDate.id!));
					addedUserDates.splice(0, addedUserDates.length);
					clear();
					clearForm();
					closeDialog(undefined);
				}}
				onOk={() => {
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					validateProduct().then(() => {
						setIsUnsavedProgressDialogOpen(false);
					});
				}}
				titleLabel={labels.warningUnsavedChanges.title}
				messageLabel={labels.warningUnsavedChanges.description}
			/>
		</>
	);
};
